import React, { useCallback } from 'react';
import { Table, Toast, Tabs } from '../../../UiKit';
import Shimmer from 'sushiweb/Shimmer';
import Switch from 'sushiweb/Switch';
import { red } from 'sushiweb/tokens/color';
import { EmptyState } from '../../commons';
import { userModel } from '../../../api';
import { InvalidSearchImg } from '../../../images/images';
import { useState } from 'react';
import { useEffect } from 'react';
import { getFeatureTableTemplate, getUsagesTableTemplate } from './utils';

const links = [
  {
    title: 'Concierge support',
    tab: 'concierge_support',
  },
  {
    title: 'Free delivery',
    tab: 'free_delivery',
  },
  {
    title: 'No batching order',
    tab: 'no_batching_order',
  },
];

const headings = {
    concierge_support: [
      { name: 'ID', key: 1 },
      { name: 'Added by', key: 2 },
      { name: 'Valid till', key: 3 },
      { name: 'Status', key: 4 },
      { name: 'Used', key: 5 },
    ],
    usages_table: [
      { name: 'Order ID', key: 1 },
      { name: 'Date', key: 2 },
      { name: 'status', key: 3 },
      { name: 'Trasaction ID', key: 4 },
    ],
  },
  emptyDatatMessage = `There is no data available for this order`;

export const DelightFeature = ({ userId = null }) => {
  const [delightFeatureDetails, setDelightFeatureDetails] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [usagesResponse, setUsagesResponse] = useState(null);
  const [toastData, setToastData] = useState(null);
  const [activeTab, setActiveTab] = useState('concierge_support');
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const setToastError = (data) => {
    let toastObj = {
      message: data.message || 'Something went wrong!',
      isError: data.isError || !data.status,
    };
    setToastData(toastObj);
  };

  const fetchUserDelightFeatureDetails = useCallback(() => {
    let queryStrings = {
        user_id: userId,
      },
      url = 'view_delight_features';

    return userModel
      .getUserDelightFeatureDetails(queryStrings, url)
      .then((res) => {
        if (!res || res.isError) {
          return setToastError(res);
        }
        setDelightFeatureDetails(res);
        setLoading(false);
      })
      .catch((err) => {
        setToastError(err);
        setLoading(false);
      });
  }, [userId, setDelightFeatureDetails]);

  useEffect(() => {
    fetchUserDelightFeatureDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUsagesTableRows = useCallback(() => {
    if (!usagesResponse) return null;
    const { data } = usagesResponse;
    const rowsData = [];
    data &&
      data.map((item) => {
        const usageObj = getUsagesTableTemplate(item);
        rowsData.push(usageObj);
        return true;
      });
    return rowsData;
  }, [usagesResponse]);

  const getUsagesTable = useCallback(() => {
    const tableData = {
      headings: headings['usages_table'],
      rows: getUsagesTableRows(),
    };
    return tableData;
  }, [getUsagesTableRows]);

  const openUsageTable = useCallback(
    (item) => () => {
      if (selectedFeatureId === item.feature_id) {
        setSelectedFeatureId(null);
        return;
      }
      let url = item.used.url,
        queryStrings = {
          user_id: userId,
          feature_id: item.feature_id,
        };

      return userModel
        .getUserDelightFeatureDetails(queryStrings, url)
        .then((res) => {
          if (!res || res.isError) {
            return;
          }
          setSelectedFeatureId(item.feature_id);
          setUsagesResponse(res);
        })
        .catch((err) => {});
    },
    [selectedFeatureId, userId],
  );

  const setFeatureStatus = useCallback(
    (flag, item) => {
      let url = item.active.url,
        queryStrings = {
          user_id: userId,
          feature_id: item.feature_id,
          is_active: flag ? 1 : 0,
        };

      return userModel
        .getDelightFeatureStatus(queryStrings, url)
        .then((res) => {
          if (!res || res.isError || !res.status) {
            return setToastError(res);
          }
          item.active.value = flag ? 1 : 0;
          setToastError(res);
        })
        .catch((err) => {
          setToastError(err);
        });
    },
    [userId],
  );

  const getActiveState = useCallback(
    (item) => {
      let template = null;

      if (item.active?.type === 'button') {
        template = (
          <div>
            <Switch
              color={red.z500}
              active={item.active.value ? true : false}
              onToggle={(flag) => setFeatureStatus(flag, item)}
            />
            <p className='lighter'>
              {item.active.value ? 'Active' : 'Inactive'}
            </p>
          </div>
        );
      }

      if (item.active?.type === 'text') {
        template = <p className='lighter'>{item.active.value}</p>;
      }

      return template;
    },
    [setFeatureStatus],
  );

  const getFeatureTableRows = useCallback(
    (details) => {
      const rowsData = [];
      details &&
        details.map((item) => {
          const isActive = selectedFeatureId === item.feature_id,
            message = usagesResponse?.message || '',
            detailsObj = getFeatureTableTemplate(
              item,
              isActive,
              message,
              getUsagesTable,
              getActiveState,
              openUsageTable,
            );
          rowsData.push(detailsObj);
          return true;
        });
      return rowsData;
    },
    [
      selectedFeatureId,
      usagesResponse,
      getUsagesTable,
      openUsageTable,
      getActiveState,
    ],
  );

  const getFeatureTableData = useCallback(
    (details) => {
      const tableData = {
        headings: headings[activeTab],
        rows: getFeatureTableRows(details),
      };
      return tableData;
    },
    [activeTab, getFeatureTableRows],
  );

  const setActiveAction = useCallback((action, index) => {
    setActiveTab(action.tab);
    setActiveTabIndex(index);
  }, []);

  if (isLoading) {
    return <Shimmer height='300px' width='100%' />;
  }

  if (!delightFeatureDetails.data) {
    return (
      <EmptyState
        img={InvalidSearchImg}
        heading={delightFeatureDetails.message || emptyDatatMessage}
        isPop={true}
      />
    );
  }

  return (
    <div className='userPromoDetails modalContent'>
      <div className='pb20'>
        <Tabs
          activeIndex={activeTabIndex}
          links={links}
          setActiveAction={setActiveAction}
        />
      </div>
      <div>
        {delightFeatureDetails.data[activeTab] &&
        delightFeatureDetails.data[activeTab].length ? (
          <Table
            tableData={getFeatureTableData(
              delightFeatureDetails.data[activeTab],
            )}
          />
        ) : (
          <div className='greyStrip'>
            <p className='lighter'>{emptyDatatMessage}</p>
          </div>
        )}
      </div>
      <Toast
        show={!!toastData}
        align='top'
        text={toastData?.message}
        type={toastData?.isError ? 'error' : 'success'}
        onClose={() => setToastData(null)}
      />
    </div>
  );
};
